import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useErrorText } from '../../shared/api/useErrorText';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../shared/auth/AuthContext';
import { TextField, Button } from '../../shared/ui';
import { api } from '../../shared/api/client';
import { HOME } from '../../shared/layout/navigation';

interface VerifyForm {
  code: string;
}

export function VerifyPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const { verify } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit } = useForm<VerifyForm>();

  const onSubmit = async (data: VerifyForm) => {
    setError(null);
    setSubmitting(true);
    try {
      await verify(email, data.code);
      navigate(HOME, { replace: true });
    } catch (e: any) {
      setError(errorText(e, 'auth.verificationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    await api.post('/auth/verify/resend', { email });
  };

  return (
    <div className="bg-surface font-body-md text-on-surface flex items-center justify-center min-h-screen">
      <main className="w-full max-w-md px-6 py-12">
        <div className="w-full max-w-md bg-surface-container-lowest rounded-3xl border border-hairline p-6 sm:p-10 shadow-soft relative overflow-hidden mx-auto">
          <div className="absolute -top-20 -right-20 w-48 h-48 bg-tertiary-fixed opacity-70 rounded-full blur-3xl pointer-events-none"></div>
          <div className="relative z-10">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-container-high mb-6 shadow-sm">
                <span className="material-symbols-outlined filled text-4xl text-primary">
                  mark_email_read
                </span>
              </div>
              <h1 className="font-headline-xl text-headline-xl text-primary mb-2">
                {t('auth.verifyTitle')}
              </h1>
              <p className="font-body-md text-body-md text-on-surface-variant">
                {t('auth.verifySubtitle')}
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <TextField
                icon="pin"
                placeholder={t('auth.verificationCode')}
                type="text"
                inputMode="numeric"
                maxLength={6}
                {...register('code', { required: true, minLength: 6 })}
              />

              {error && <p className="text-error text-body-md text-center">{error}</p>}

              <Button type="submit" className="w-full" disabled={submitting}>
                {t('auth.verify')}
                <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <button
                onClick={resend}
                className="font-label-sm text-label-sm text-primary hover:underline"
              >
                {t('auth.resend')}
              </button>
              <div>
                <Link
                  to="/login"
                  className="font-body-md text-body-md text-on-surface-variant hover:text-on-surface"
                >
                  {t('auth.signIn')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
